import { useEffect, useRef, useState } from "react";
import Draggable from "react-draggable";

import { useGetDatasetMetadataQuery } from "@/api/dataset/useDatasetAPI";
import { SpinnerIcon } from "@/assets/icons";
import useOnClickOutside from "@/hooks/ui/useClickOutside";
import { Dataset } from "@/types/dataset";

import { twMerge } from "tailwind-merge";

import EditPopupAttribute from "./EditPopupAttribute";

// Offset dictionary
export const OFFSET_MAPPING = {
  default: { top: 0, left: 320 },
  catchment: { top: -30, left: -110 }
  // can add more if needed
};

interface Props {
  dataset: Dataset;
  anchorEl: HTMLElement;
  offsetId?: keyof typeof OFFSET_MAPPING;
  onClose: () => void;
}

export const EditPopup = ({
  dataset,
  anchorEl,
  offsetId = "default",
  onClose
}: Props) => {
  const ref = useRef<HTMLDialogElement>(null);
  const [overlayElement, setOverlayElement] = useState<HTMLElement | null>(
    null
  );
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties>({
    display: "none"
  });

  const { data: selectedDatasetMetadata } = useGetDatasetMetadataQuery(
    dataset.data?.id ?? ""
  );

  const offsetTop = OFFSET_MAPPING[offsetId].top;
  const offsetLeft = OFFSET_MAPPING[offsetId].left;

  useEffect(() => {
    // Safely access DOM element after component mounts
    setOverlayElement(document.getElementById("overlays"));
  }, []);

  useOnClickOutside(ref, onClose, [overlayElement]);

  // Calculate position only once when the component mounts or anchorEl changes
  useEffect(() => {
    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect();

      // Calculate initial position
      let top = rect.bottom - 20;

      // Get viewport height
      const viewportHeight = window.innerHeight;

      const elementHeight = ref.current?.offsetHeight || 400; // Fallback to estimate

      // Check if element would be clipped by viewport
      const isClippedBottom = top + elementHeight > viewportHeight;

      if (isClippedBottom) {
        top = Math.max(10, viewportHeight - elementHeight - 10); // Position higher
      }
      setPopupStyle({
        display: "block",
        position: "fixed" as const,
        top: top + offsetTop,
        left: rect.left + offsetLeft,
        transform: "none"
      });
    }
  }, [anchorEl, ref]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Draggable>
      <dialog
        ref={ref}
        aria-modal="true"
        style={popupStyle}
        className={twMerge(
          "z-[99] font-sans border-gainsboro bg-white rounded-lg w-[338px]",
          "flex flex-col p-[16px] h-auto max-h-[90vh]"
        )}
      >
        {selectedDatasetMetadata ? (
          <EditPopupAttribute
            dataset={dataset}
            selectedDataset={selectedDatasetMetadata ?? null}
            onClose={onClose}
          />
        ) : (
          <SpinnerIcon
            className="justify-self-center spin-animation my-4"
            title="edit-popup-loading"
            width={60}
            height={60}
          />
        )}
      </dialog>
    </Draggable>
  );
};

export default EditPopup;
